/*
  Modo de precio de una cotización — lectura y escritura.

  Los cuatro modos no son una columna nueva: son la lectura conjunta de tres
  campos que ya mandan el documento (`mostrar_precios`, `igv_porcentaje` y
  `branding.presupuesto_mas_igv`). Agregar una cuarta fuente de verdad habría
  dejado dos versiones del mismo hecho esperando a desincronizarse; lo que
  faltaba era leerlos como lo que son: un conjunto excluyente.

  En el editor estaban sueltos — un número y un checkbox en cards distintas —,
  así que para sacar un «USD 9,950 + IGV» había que escribir SQL.
*/

import type { CotizacionBranding, Marca, ModoPrecio } from './types'

export const MODOS_PRECIO: { value: ModoPrecio; label: string; hint: string }[] = [
  { value: 'total', label: 'Total con IGV', hint: 'El banner muestra el total, IGV incluido.' },
  { value: 'mas_igv', label: 'Monto + IGV', hint: 'El banner muestra el monto sin IGV y la leyenda «+ IGV».' },
  { value: 'sin_igv', label: 'Sin IGV', hint: 'El documento no lleva impuesto (IGV 0 %).' },
  { value: 'sin_precios', label: 'Sin precios (propuesta de alcance)', hint: 'Se ve qué se hace, no cuánto cuesta: sin importes, sin totales y sin datos bancarios.' },
]

/** Deriva el modo de una cotización ya guardada. */
export function modoDesdeCotizacion(cot: {
  mostrar_precios?: boolean | null
  igv_porcentaje?: number | null
  branding?: CotizacionBranding | null
}): ModoPrecio {
  if (cot.mostrar_precios === false) return 'sin_precios'
  if (cot.branding?.presupuesto_mas_igv) return 'mas_igv'
  if (!cot.igv_porcentaje) return 'sin_igv'
  return 'total'
}

/** Los campos que el modo escribe en la cotización. */
export function camposDesdeModo(modo: ModoPrecio, igvPct: number): { mostrar_precios: boolean; igv_porcentaje: number } {
  return {
    mostrar_precios: modo !== 'sin_precios',
    igv_porcentaje: modo === 'sin_igv' ? 0 : igvPct,
  }
}

/** Si el modo deja al usuario elegir el porcentaje de IGV. */
export function modoLlevaIgv(modo: ModoPrecio): boolean {
  return modo === 'total' || modo === 'mas_igv'
}

/**
 * Arma el `branding` que se guarda: identidad de la marca (si hay) + el modo de
 * precio. Devuelve `null` cuando no queda nada que guardar, para no dejar un
 * objeto vacío que las superficies tengan que interpretar.
 *
 * `brandingManual` es el objeto que ya tenía la cotización cuando su marca no
 * está en el catálogo (branding puesto por SQL): se conserva tal cual en vez de
 * pisarse, que es lo que pasaría si lo rearmáramos desde un catálogo que no la
 * conoce.
 */
export function armarBranding(opts: {
  modo: ModoPrecio
  marca?: Marca | null
  marca2?: Marca | null
  brandingManual?: CotizacionBranding | null
}): CotizacionBranding | null {
  const { modo, marca, marca2, brandingManual } = opts

  const b: CotizacionBranding = brandingManual
    ? { ...brandingManual }
    : marca
      ? {
          nombre: marca.nombre,
          website: marca.website ?? '',
          // El data URI, no `logo_url`: Gotenberg no descarga recursos externos.
          logo_url: marca.logo_data_uri ?? '',
        }
      : {}

  if (marca2) {
    b.nombre_2 = marca2.nombre
    b.logo_url_2 = marca2.logo_data_uri ?? ''
  } else if (!brandingManual) {
    delete b.nombre_2
    delete b.logo_url_2
  }

  if (modo === 'mas_igv') b.presupuesto_mas_igv = true
  else delete b.presupuesto_mas_igv

  return Object.keys(b).length > 0 ? b : null
}

/**
 * Busca en el catálogo la marca que corresponde a un nombre ya guardado en el
 * documento. El nombre es la clave porque es lo único que el snapshot y el
 * catálogo comparten (el logo del documento es una copia, no una referencia).
 */
export function marcaPorNombre(marcas: Marca[], nombre?: string | null): Marca | null {
  if (!nombre) return null
  const n = nombre.trim().toLowerCase()
  return marcas.find(m => m.nombre.trim().toLowerCase() === n) ?? null
}
